import "@dxos/node-std/globals";

// src/stream.ts
import { Context } from "@dxos/context";
import { invariant } from "@dxos/invariant";
import { log } from "@dxos/log";
import { throwUnhandledError } from "@dxos/util";
var __dxlog_file = "/__w/dxos/dxos/packages/common/codec-protobuf/src/stream.ts";
var Stream = class _Stream {
  /**
  * Consumes the entire stream to the end until it closes and returns a promise with the resulting items.
  */
  static consume(stream) {
    return new Promise((resolve) => {
      const items = [];
      stream.onReady(() => {
        items.push({
          ready: true
        });
      });
      stream.subscribe((data) => {
        items.push({
          data
        });
      }, (error) => {
        if (error) {
          items.push({
            closed: true,
            error
          });
        } else {
          items.push({
            closed: true
          });
        }
        resolve(items);
      });
    });
  }
  static async consumeData(stream) {
    const entries = await _Stream.consume(stream);
    const res = [];
    for (const entry of entries) {
      if ("data" in entry) {
        res.push(entry.data);
      } else if ("closed" in entry && entry.closed === true) {
        if (entry.error) {
          throw entry.error;
        } else {
          break;
        }
      }
    }
    return res;
  }
  static async first(stream) {
    return new Promise((resolve, reject) => {
      stream.subscribe((data) => {
        resolve(data);
        void stream.close();
      }, (error) => {
        if (error) {
          reject(error);
        } else {
          resolve(void 0);
        }
      });
    });
  }
  /**
  * Maps all data coming through the stream.
  */
  static map(source, map) {
    return new _Stream(({ ready, next, close }) => {
      source.onReady(ready);
      source.subscribe((data) => next(map(data)), close);
      return () => source.close();
    });
  }
  /**
  * Converts Promise<Stream<T>> to Stream<T>.
  */
  static unwrapPromise(streamPromise) {
    if (streamPromise instanceof _Stream) {
      return streamPromise;
    }
    return new _Stream(({ ready, next, close }) => {
      streamPromise.then((stream) => {
        stream.onReady(ready);
        stream.subscribe(next, close);
      }, (err) => {
        close(err);
      });
      return () => {
        streamPromise.then((stream) => stream.close(), (err) => {
        });
      };
    });
  }
  static fromPromise(promise) {
    return new _Stream(({ ready, next, close }) => {
      promise.then((data) => {
        ready();
        next(data);
        close();
      }, (err) => {
        close(err);
      });
    });
  }
  _ctx;
  _messageHandler = void 0;
  _closeHandler = void 0;
  _readyHandler = void 0;
  _isClosed = false;
  _closeError = void 0;
  _producerCleanup = void 0;
  _readyPromise;
  _resolveReadyPromise;
  _isReady = false;
  /**
  * Buffer messages before subscription. Set to null when buffer is no longer needed.
  */
  _buffer = [];
  constructor(producer, parentCtx) {
    this._readyPromise = new Promise((resolve) => {
      this._resolveReadyPromise = resolve;
    });
    this._ctx = new Context({
      parent: parentCtx,
      onError: (err) => {
        if (this._isClosed) {
          return;
        }
        this._isClosed = true;
        this._closeError = err;
        this._producerCleanup?.(err);
        this._closeHandler?.(err);
        void this._ctx.dispose();
      }
    }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 143 });
    this._ctx.onDispose(() => this.close());
    try {
      const producerCleanup = producer({
        ctx: this._ctx,
        ready: () => {
          this._markAsReady();
        },
        next: (msg) => {
          if (this._isClosed) {
            log("Stream is closed, dropping message.", void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 165, S: this });
            return;
          }
          this._markAsReady();
          if (this._messageHandler) {
            try {
              this._messageHandler(msg);
            } catch (err) {
              throwUnhandledError(err);
            }
          } else {
            invariant(this._buffer, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 177, S: this, A: ["this._buffer", ""] });
            this._buffer.push(msg);
          }
        },
        close: (err) => {
          if (this._isClosed) {
            return;
          }
          this._isClosed = true;
          this._closeError = err;
          this._producerCleanup?.(err);
          try {
            this._closeHandler?.(err);
          } catch (err2) {
            throwUnhandledError(err2);
          }
          void this._ctx.dispose();
        }
      });
      if (producerCleanup) {
        this._producerCleanup = producerCleanup;
      }
    } catch (err) {
      this._ctx.raise(err);
    }
  }
  _markAsReady() {
    if (!this._isReady) {
      this._isReady = true;
      this._readyHandler?.();
      this._resolveReadyPromise();
    }
  }
  // TODO(burdon): Can this be cancelled?
  subscribe(onMessage, onClose) {
    invariant(!this._messageHandler, "Stream is already subscribed to.", { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 213, S: this, A: ["!this._messageHandler", "'Stream is already subscribed to.'"] });
    invariant(!this._closeHandler, "Stream is already subscribed to.", { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 214, S: this, A: ["!this._closeHandler", "'Stream is already subscribed to.'"] });
    invariant(this._buffer, void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 215, S: this, A: ["this._buffer", ""] });
    for (const message of this._buffer) {
      try {
        onMessage(message);
      } catch (err) {
        throwUnhandledError(err);
      }
    }
    this._buffer = null;
    if (this._isClosed) {
      onClose?.(this._closeError);
      return;
    }
    this._messageHandler = onMessage;
    this._closeHandler = onClose;
  }
  /**
  * Resolves when stream is ready.
  */
  // TODO(burdon): Gather all callbacks into single observer.
  waitUntilReady() {
    return this._readyPromise;
  }
  /**
  * Registers a callback to be called when stream is ready.
  */
  onReady(onReady) {
    invariant(!this._readyHandler, "Stream already has a handler for the ready event.", { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 242, S: this, A: ["!this._readyHandler", "'Stream already has a handler for the ready event.'"] });
    this._readyHandler = onReady;
    if (this._isReady) {
      onReady();
    }
  }
  /**
  * Close the stream and dispose of any resources.
  */
  async close() {
    if (this._isClosed) {
      return;
    }
    this._isClosed = true;
    this._producerCleanup?.();
    this._closeHandler?.(void 0);
    await this._ctx.dispose();
    this._messageHandler = void 0;
    this._closeHandler = void 0;
    this._producerCleanup = void 0;
  }
};

export {
  Stream
};
//# sourceMappingURL=chunk-V5FNK5HH.mjs.map
